import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const fmt = new Intl.NumberFormat("en-IN");
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);
  const [kind, setKind] = useState("consultation");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setAmount(data.amount); setKind(data.kind || "consultation"); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { console.warn("Payment status poll failed, retrying", e?.message); }
      tries += 1;
      if (tries >= MAX_POLLS) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const COPY = {
    order: { title: "Order placed", body: "Your payment is secured. Our desk will confirm the off-market transfer and update your portfolio shortly — you'll see it as 'Pending confirmation' in My Account. If you haven't yet, complete KYC there so we can transfer shares to your demat." },
    wallet: { title: "Wallet topped up", body: "Your wallet balance has been updated. You can use it towards future purchases and consultations." },
    consultation: { title: "Payment received", body: "Thank you. A desk advisor will reach out within one business day to schedule your consultation. A receipt is on its way to your inbox." },
  };
  const copy = COPY[kind] || COPY.consultation;
  const retry = kind === "order" ? "/#opportunities" : kind === "wallet" ? "/client/portfolio" : "/#consult";

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen flex-col items-center justify-center bg-[#FFFFFF] px-6 text-center text-slate-900">
      <Link to="/" className="mb-10 flex items-center gap-3">
        <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
        <span className="font-display text-lg font-semibold tracking-[0.18em]">PRE-IPO <span className="text-[#6D28D9]">EXPERT</span></span>
      </Link>

      {state === "checking" && (
        <>
          <Loader2 className="h-9 w-9 animate-spin text-[#6D28D9]" />
          <p data-testid="payment-checking" className="mt-6 font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Confirming your payment…</p>
        </>
      )}

      {state === "paid" && (
        <div data-testid="payment-paid" className="max-w-md border border-emerald-600/30 bg-emerald-600/[0.06] p-10">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-700" />
          <h1 className="font-display mt-5 text-2xl font-semibold tracking-tight text-emerald-800">{copy.title}{amount ? ` — ₹${fmt.format(amount)}` : ""}</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{copy.body}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link data-testid="payment-account-link" to="/client/dashboard" className="border border-[#6D28D9]/40 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9] transition-colors hover:bg-[#6D28D9]/10">My account</Link>
            <Link to="/" className="border border-slate-200 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]">Back to site</Link>
          </div>
        </div>
      )}

      {state === "pending" && (
        <div data-testid="payment-pending" className="max-w-md border border-[#6D28D9]/25 bg-[#6D28D9]/[0.05] p-10">
          <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#6D28D9]" />
          <h1 className="font-display mt-5 text-xl font-semibold tracking-tight">Still processing</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">Your payment is taking a little longer to confirm. If it was completed, you'll receive a receipt by email shortly — no need to pay again.</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 border border-[#6D28D9]/40 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
        </div>
      )}

      {state === "error" && (
        <div data-testid="payment-error" className="max-w-md border border-rose-600/30 bg-rose-600/[0.06] p-10">
          <AlertTriangle className="mx-auto h-9 w-9 text-rose-700" />
          <h1 className="font-display mt-5 text-xl font-semibold tracking-tight text-rose-800">We couldn't confirm this payment</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">If any amount was deducted it will be reversed automatically. You can try booking again or reach the desk on WhatsApp.</p>
          <Link to={retry} className="mt-8 inline-flex items-center gap-2 border border-[#6D28D9]/40 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]">Try again</Link>
        </div>
      )}
    </div>
  );
}
